// src/pages/book/BookDetailPage.jsx
import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBooks } from '../../hooks/useBooks';
import { useCart } from '../../hooks/useCart';
import { useReviews } from '../../contexts/ReviewContext'; // ← Added
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StarRating from 'react-stars';
import ReviewSection from '../../components/review/ReviewSection';

const BookDetailPage = () => {
  const { id } = useParams();
  const { books, loading: booksLoading } = useBooks();
  const { addToCart } = useCart();
  const { getReviews, loadReviews } = useReviews();

  const book = books.find(b => b.id === id);
  const reviews = getReviews(id) || [];

  // Load reviews when page loads
  useEffect(() => {
    if (book) {
      loadReviews(id);
    }
  }, [id, book, loadReviews]);

  if (booksLoading) return <LoadingSpinner />;

  if (!book) {
    return (
      <div className="book-not-found container">
        <h2>Book Not Found</h2>
        <Link to="/shop" className="back-to-shop-link">
          ← Back to Shop
        </Link>
      </div>
    );
  }

  // Calculate real average rating
  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div className="book-detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="book-breadcrumb">
          <Link to="/" className="breadcrumb-link">Home</Link>
          <span className="breadcrumb-separator">/</span>
          <Link to="/shop" className="breadcrumb-link">Shop</Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{book.title}</span>
        </nav>

        {/* Main Grid */}
        <div className="book-detail-grid">
          {/* Left: Book Cover */}
          <div className="book-cover-section">
            <div className="book-cover-container">
              <img
                src={book.imageUrl || '/placeholder-book.jpg'}
                alt={book.title}
                className="book-cover-img"
              />
            </div>
          </div>

          {/* Right: Book Info */}
          <div className="book-info-section">
            <h1 className="book-title">{book.title}</h1>
            <p className="book-author">by {book.author}</p>

            {/* Real Rating from Reviews */}
            <div className="book-rating">
              {avgRating ? (
                <>
                  <StarRating
                    value={parseFloat(avgRating)}
                    edit={false}
                    size={28}
                    color2="#D9A441"
                  />
                  <span className="rating-count">
                    ({avgRating} / 5 from {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
                  </span>
                </>
              ) : (
                <span className="no-reviews">No reviews yet</span>
              )}
            </div>

            {/* Price */}
            <div className="book-price-large">
              ${book.price?.toFixed(2)} <span className="currency">CAD</span>
            </div>

            {/* Actions */}
            <div className="book-actions">
              <button
                onClick={() => addToCart(book)}
                className="btn-primary btn-add-to-cart"
              >
                Add to Cart
              </button>
              <Link to="/cart" className="btn-secondary btn-buy-now">
                Buy Now
              </Link>
            </div>

            {/* Meta Info */}
            <div className="book-meta">
              <div className="meta-item">
                <strong>Language:</strong> Nepali
              </div>
              <div className="meta-item">
                <strong>Pages:</strong> {book.pages || 'N/A'}
              </div>
              <div className="meta-item">
                <strong>Genre:</strong> {book.genre || 'N/A'}
              </div>
            </div>

            <hr className="section-divider" />

            {/* Description */}
            <div className="book-description-section">
              <h2 className="description-heading">Description</h2>
              <p className="description-text">
                {book.description ||
                  "A timeless masterpiece of Nepali literature that explores themes of love, sacrifice, and human resilience against the backdrop of Nepal's rich cultural heritage."}
              </p>
            </div>

            {/* Reviews Section */}
            <hr className="section-divider" />
            <ReviewSection bookId={book.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookDetailPage;